/**
 * The inverse of `studioColumnAxis`: a saved report's `column_tuples` and derived `column_axis` entries
 * read back into column cards, so editing a report shows the columns it was saved with rather than the
 * deck's defaults.
 */

import { v4 as uuid } from 'uuid';
import type { ColumnAxisEntry, ReportSpec } from 'src/components/star-grid/api/reportEngineTypes';
import type { StudioCatalog, StudioColumn, StudioScenarioBlock, StudioVarianceBlock } from 'src/components/star-grid/reportStudio.types';
import { DEFAULT_CURRENCY, VARIANCE_CALC, VARIANCE_PCT_CALC, columnBaseLabel, periodToken } from 'src/components/star-grid/utils/studioColumnAxis';

const SPACER_SCENARIO = 'EMPTY';

interface Lookup {
  readonly scenarioId: (label: string) => string | undefined;
  readonly periodId: (token: string) => string | undefined;
  readonly currencyId: (label: string) => string | undefined;
  readonly metricId: string;
}

const collapse = (label: string): string => label.split(/\s+/).filter(Boolean).join(' ');

function lookups(catalog: StudioCatalog, datasetId: string): Lookup {
  const scenarios = new Map(catalog.scenarios.map((option) => [collapse(option.label), option.id]));
  const periods = new Map<string, string>();
  for (const period of catalog.periods) {
    const token = periodToken(period.months);
    if (token && !periods.has(token)) periods.set(token, period.id);
  }
  const dataset = catalog.datasets.find((entry) => entry.id === datasetId);
  const currencies = new Map((dataset?.currencies ?? []).map((option) => [option.label, option.id]));
  return {
    scenarioId: (label) => scenarios.get(collapse(label)),
    periodId: (token) => periods.get(token),
    currencyId: (label) => currencies.get(label),
    metricId: dataset?.metrics[0]?.id ?? ''
  };
}

/** `USD@GQ3-26` -> `['USD', 'GQ3-26']`; the part after `@` is the scenario whose rates are held. */
const splitCurrency = (member: string): readonly [string, string | undefined] => {
  const [currency, fxScenario] = member.split('@');
  return [currency || DEFAULT_CURRENCY, fxScenario];
};

/** Where each base label landed, so a variance can name its two cards and their periods. */
type Placed = Map<string, { readonly blockId: string; readonly periodId: string }>;

function scenarioBlock(tuple: readonly string[], lookup: Lookup): StudioScenarioBlock | undefined {
  const [scenario = '', currencyMember = DEFAULT_CURRENCY] = tuple;
  const scenarioId = lookup.scenarioId(scenario);
  if (!scenarioId) return undefined;
  const [currency, fxScenario] = splitCurrency(currencyMember);
  const currencyId = lookup.currencyId(currency);
  return {
    id: uuid(),
    kind: 'scenario',
    label: scenario,
    scenario_id: scenarioId,
    version_id: null,
    period_ids: [],
    metric_id: lookup.metricId,
    currency_ids: currencyId ? [currencyId] : [],
    fx_policy_id: fxScenario ? lookup.scenarioId(fxScenario) ?? null : null
  };
}

/** The `left|right` pair key a derived entry addresses, so a pct twin pairs with its variance. */
const sidesKey = (entry: ColumnAxisEntry): string => `${entry.left ?? ''}\u0000${entry.right ?? ''}`;

/** Consecutive variance entries between the same two cards were ONE card on save. */
function varianceBlocks(entries: readonly ColumnAxisEntry[], placed: Placed, lookup: Lookup): StudioVarianceBlock[] {
  // A `variance_pct` entry rides alongside its variance (same sides, same position), so its presence —
  // not a card of its own — is what `show_percent` round-trips through.
  const percentSides = new Set(entries.filter((entry) => entry.calc === VARIANCE_PCT_CALC).map(sidesKey));
  const blocks: StudioVarianceBlock[] = [];
  for (const entry of entries) {
    const leftPlaced = placed.get(entry.left ?? '');
    const rightPlaced = placed.get(entry.right ?? '');
    if (entry.calc !== VARIANCE_CALC || !leftPlaced || !rightPlaced) continue;
    // Save swaps the sides for `compare_minus_base`; no direction (an older report) means `left = base`.
    const direction = entry.variance_direction ?? 'base_minus_compare';
    const swapped = direction === 'compare_minus_base';
    const basePlaced = swapped ? rightPlaced : leftPlaced;
    const comparePlaced = swapped ? leftPlaced : rightPlaced;
    const showPercent = percentSides.has(sidesKey(entry));
    const pair = { base: basePlaced.periodId, compare: comparePlaced.periodId };
    const last = blocks[blocks.length - 1];
    if (last && last.base_block_id === basePlaced.blockId && last.compare_block_id === comparePlaced.blockId && last.direction === direction) {
      blocks[blocks.length - 1] = { ...last, period_pairs: [...last.period_pairs, pair] };
      continue;
    }
    const fxScenario = entry.fx_scenario ? splitCurrency(entry.fx_scenario)[1] : undefined;
    blocks.push({
      id: uuid(),
      kind: 'variance',
      label: '',
      base_block_id: basePlaced.blockId,
      compare_block_id: comparePlaced.blockId,
      period_pairs: [pair],
      direction,
      fx_policy_id: fxScenario ? lookup.scenarioId(fxScenario) ?? null : null,
      show_percent: showPercent
    });
  }
  return blocks;
}

/**
 * The column cards a saved spec states, in shelf order; `undefined` when it states none, so the deck's
 * own columns stand.
 */
export function studioColumnsFromSpec(catalog: StudioCatalog, datasetId: string, spec: ReportSpec): StudioColumn[] | undefined {
  const tuples = spec.column_tuples ?? [];
  if (tuples.length === 0) return undefined;
  const lookup = lookups(catalog, datasetId);
  const derived = spec.column_axis.filter((entry) => entry.calc && entry.calc !== 'spacer');
  const derivedAt = (index: number) => derived.filter((entry) => Math.min(entry.position ?? tuples.length, tuples.length) === index);
  const placed: Placed = new Map();
  // Variances resolve only after every scenario card is placed: a base or compare card may sit to their right.
  type Slot = { readonly card: StudioScenarioBlock } | { readonly variances: readonly ColumnAxisEntry[] };
  const slots: Slot[] = [];
  let open: StudioScenarioBlock | undefined;
  const closeCard = (): void => {
    if (open && open.period_ids.length > 0) slots.push({ card: open });
    open = undefined;
  };
  tuples.forEach((tuple, index) => {
    const variances = derivedAt(index);
    if (variances.length > 0) {
      closeCard();
      slots.push({ variances });
    }
    const [scenario = '', currencyMember = DEFAULT_CURRENCY, token = ''] = tuple;
    if (scenario === SPACER_SCENARIO) return closeCard();
    if (open && (open.label !== scenario || tuples[index - 1]?.[1] !== currencyMember)) closeCard();
    open = open ?? scenarioBlock(tuple, lookup);
    const periodId = lookup.periodId(token);
    if (!open || !periodId) return;
    open = { ...open, period_ids: [...open.period_ids, periodId] };
    placed.set(columnBaseLabel(scenario, splitCurrency(currencyMember)[0], token), { blockId: open.id, periodId });
  });
  closeCard();
  const trailing = derivedAt(tuples.length);
  if (trailing.length > 0) slots.push({ variances: trailing });
  return slots.flatMap((slot): StudioColumn[] => ('card' in slot ? [slot.card] : varianceBlocks(slot.variances, placed, lookup)));
}
